import './admin.css'

import { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { Input, PageHeader, Spinner } from '../../../../Components'
import { cap } from '../../../../State'
import { translations } from '../../../../translations'
import { AdminEditorActions } from '../../Components/AdminTableActions'
import { saveSociety, useSociety } from './Functions'

const language = cap.language.state || 'IT'
const t = translations[language]

// Boolean options for dropdowns
const booleanOptions = [
  { label: t.abilitato, value: 1 },
  { label: t.admDisabilitato, value: 0 }
]

// Display names for sections (the section keys below are used as logic identifiers
// for expand/collapse state, so they must stay stable and only the label is translated)
const sectionDisplayNames = {
  'Informazioni Base': t.admSezioneInfoBase,
  'Impostazioni AI': t.admSezioneImpostazioniAI,
  'Integrazione B4B': t.admSezioneIntegrazioneB4B,
  'Integrazione Atlante': t.admSezioneIntegrazioneAtlante,
  'Integrazione Dinamico (PHP e WSDynPkg)': t.admSezioneIntegrazioneDinamico,
  'Email e Comunicazioni': t.admSezioneEmailComunicazioni
}

// Dynamic PKG Target options
const dynamicPKGTargetOptions = [
  { label: 'Test', value: 'Test' },
  { label: 'Production', value: 'Production' }
]

// Field configuration organized by sections
const fieldSections = {
  'Informazioni Base': {
    societyName: { label: t.admNome, description: t.admNomeDesc },
    codSociety: {
      label: t.admCodiceSocieta,
      description: t.admCodiceSocietaDesc
    },
    codMastroCliente: {
      label: t.admCodiceMastroCliente,
      description: t.admCodiceMastroClienteDesc
    },
    tomcatCode: { label: t.admCodiceTomcat, description: t.admCodiceTomcatDesc }
  },
  'Impostazioni AI': {
    maxAIMessages: {
      label: t.admNumeroMassimoMessaggiAI,
      description: t.admNumeroMassimoMessaggiAIDesc
    },
    canTakeCharge: {
      label: t.admPresaInCarico,
      type: 'boolean',
      description: t.admPresaInCaricoDesc
    },
    enableReadAttachments: {
      label: t.admLetturaAllegatiEmail,
      type: 'boolean',
      description: t.admLetturaAllegatiEmailDesc
    },
    enableReport: {
      label: t.admVisualizzaBottoneReport,
      type: 'boolean',
      description: t.admVisualizzaBottoneReportDesc
    },
    enableReadSubject: {
      label: t.admLetturaOggettoEmail,
      type: 'boolean',
      description: t.admLetturaOggettoEmailDesc
    },
    enableReportOperatorInEmail: {
      label: t.admResocontoOperatore,
      type: 'boolean',
      description: t.admResocontoOperatoreDesc
    },
    enableAuto: {
      label: t.admGestioneAuto,
      type: 'boolean',
      description: t.admGestioneAutoDesc
    },
    enableCleanPPI: {
      label: t.admPuliziaPPI,
      type: 'boolean',
      description: t.admPuliziaPPIDesc
    },
    enableRioFlow: {
      label: t.admFlussoRichiestaInfoGeneriche,
      type: 'boolean',
      description: t.admFlussoRichiestaInfoGenericheDesc
    },
    enableMultiPax: {
      label: t.admGestioneGruppiPasseggeri,
      type: 'boolean',
      description: t.admGestioneGruppiPasseggeriDesc
    }
  },
  'Integrazione B4B': {
    b4bEnabled: {
      label: t.admAbilitazioneB4B,
      type: 'boolean',
      description: t.admAbilitazioneB4BDesc
    },
    enableCarnet: {
      linkTo: { flag: 'b4bEnabled', offValue: false },
      label: t.admAbilitazioneCarnet,
      type: 'boolean',
      description: t.admAbilitazioneCarnetDesc
    },
    enableArrivoEntro: {
      linkTo: { flag: 'b4bEnabled', offValue: false },
      label: t.admAbilitazioneArrivoEntro,
      type: 'boolean',
      description: t.admAbilitazioneArrivoEntroDesc
    },
    enableRimborsabile: {
      linkTo: { flag: 'b4bEnabled', offValue: false },
      label: t.admAbilitazioneRimborsabile,
      type: 'boolean',
      description: t.admAbilitazioneRimborsabileDesc
    },
    skipTrainStationSearch: {
      linkTo: { flag: 'b4bEnabled', offValue: true },
      label: t.admSaltoRicercaTreni,
      type: 'boolean',
      description: t.admSaltoRicercaTreniDesc
    },
    b4bDbUrl: { label: t.admUrlB4B, description: t.admUrlB4BDesc },
    b4bDbPort: { label: t.admPortaB4B, description: t.admPortaB4BDesc },
    b4bDbUser: { label: t.admUtenteB4B, description: t.admUtenteB4BDesc },
    b4bDbPassword: {
      label: t.admPasswordB4B,
      description: t.admPasswordB4BDesc
    },
    b4bDbName: { label: t.admNomeDbB4B, description: t.admNomeDbB4BDesc }
  },
  'Integrazione Atlante': {
    atlanteUrl: {
      label: t.admUrlAtlante,
      description: t.admUrlAtlanteDesc
    },
    userWS: { label: t.admUtenteWS, description: t.admUtenteWSDesc },
    passwordWS: {
      label: t.admPasswordWS,
      description: t.admPasswordWSDesc
    },
    googleApiKey: { label: t.admGoogleApiKey, description: t.admGoogleApiKeyDesc },
    enableMidOffice: {
      label: t.admAbilitazioneMidOffice,
      type: 'boolean',
      description: t.admAbilitazioneMidOfficeDesc
    },
    enableAtlante: {
      label: t.admAbilitazioneAtlante,
      type: 'boolean',
      description: t.admAbilitazioneAtlanteDesc
    },
    enableCampi1a10: {
      linkTo: { flag: 'enableMidOffice', offValue: false },
      label: t.admEstrapolazioneCampi1a10,
      type: 'boolean',
      description: t.admEstrapolazioneCampi1a10Desc
    },
    enableReadHistoryPax: {
      linkTo: { flag: 'enableMidOffice', offValue: false },
      label: t.admLetturaStoricoViaggiatore,
      type: 'boolean',
      description: t.admLetturaStoricoViaggiatoreDesc
    }
  },
  'Integrazione Dinamico (PHP e WSDynPkg)': {
    dinamicoPhpUrl: { label: t.admUrlDinamicoPhp, description: t.admUrlDinamicoPhpDesc },
    dinamicoDbUrl: { label: t.admUrlDbDinamico, description: t.admUrlDbDinamicoDesc },
    dinamicoDbPort: { label: t.admPortaDbDinamico, description: t.admPortaDbDinamicoDesc },
    dinamicoDbUser: {
      label: t.admUtenteDbDinamico,
      description: t.admUtenteDbDinamicoDesc
    },
    dinamicoDbPassword: {
      label: t.admPasswordDbDinamico,
      description: t.admPasswordDbDinamicoDesc
    },
    dinamicoDbName: { label: t.admNomeDbDinamico, description: t.admNomeDbDinamicoDesc },
    urlDynJavaWS: {
      label: t.admUrlJavaWsDinamico,
      description: t.admUrlJavaWsDinamicoDesc
    },
    idDynJavaWS: {
      label: t.admUtenteJavaWsDinamico,
      description: t.admUtenteJavaWsDinamicoDesc
    },
    passwordDynJavaWS: {
      label: t.admPasswordJavaWsDinamico,
      description: t.admPasswordJavaWsDinamicoDesc
    },
    codOperatore: {
      label: t.admCodiceOperatore,
      description: t.admCodiceOperatoreDesc
    },
    codProvenienza: {
      label: t.admCodiceProvenienza,
      description: t.admCodiceProvenienzaDesc
    },
    dynamicPKGTarget: {
      label: t.admTargetPacchettoDinamico,
      type: 'enum',
      description: t.admTargetPacchettoDinamicoDesc,
      options: dynamicPKGTargetOptions
    }
  },
  'Email e Comunicazioni': {
    whatsappPhone: { label: t.admTelefonoWhatsapp, description: t.admTelefonoWhatsappDesc },
    enableResumeOperator: {
      label: t.admInvioEmailRiassuntivoOperatore,
      type: 'boolean',
      description: t.admInvioEmailRiassuntivoOperatoreDesc
    },
    reportCCEmail: {
      label: t.admElencoEmailCCReport,
      type: 'array',
      description: t.admElencoEmailCCReportDesc
    },
    disableErrorMessageToOperator: {
      label: t.admBloccaEmailErroreOperatori,
      type: 'boolean',
      description: t.admBloccaEmailErroreOperatoriDesc
    },
    enableInvioNotificaFormDati: {
      label: t.admInviaNotificaFormDati,
      type: 'boolean',
      description: t.admInviaNotificaFormDatiDesc
    },
    enableSanitizeEmailNoAI: {
      label: t.admSanitizzazioneEmailSenzaAI,
      type: 'boolean',
      description: t.admSanitizzazioneEmailSenzaAIDesc
    },
    configEmailReport: {
      label: t.admConfigurazioneEmailReport,
      type: 'json',
      description: t.admConfigurazioneEmailReportDesc
    }
  }
}

// Status indicator component for boolean fields
const StatusIndicator = ({ value }) => (
  <div className={`admin-status-indicator ${value === 1 ? 'enabled' : 'disabled'}`}>
    <div className="admin-status-dot" />
  </div>
)

// BooleanSelect component for 0/1 fields
const BooleanSelect = ({ value, onChange, id }) => (
  <div className="admin-boolean-field">
    <StatusIndicator value={value} />
    <Input
      dropdown
      value={value}
      onChange={onChange}
      id={id}
      options={booleanOptions}
      optionLabel="label"
      optionValue="value"
      placeholder={t.seleziona}
    />
  </div>
)

/**
 * Verifica se un campo deve essere visibile in base alla configurazione linkTo
 * @param {Object} fieldConfig - Configurazione del campo
 * @param {Object} society - Oggetto società con i valori attuali
 * @returns {boolean} true se il campo deve essere visibile, false altrimenti
 */
const isFieldVisible = (fieldConfig, society) => {
  if (!fieldConfig.linkTo) {
    // Se non c'è linkTo, il campo è sempre visibile
    return true
  }

  const { flag } = fieldConfig.linkTo
  const flagValue = society?.[flag]

  // Per boolean fields: 1 = true, 0 = false
  // Il campo è visibile se il flag è true (1)
  // Se il flag è false (0), il campo deve essere nascosto
  if (flagValue === 0 || flagValue === false) {
    return false
  }

  return true
}

export function SocietyEditor() {
  const { societyId } = useParams()
  const navigate = useNavigate()
  const [society, setSociety, isSuccess] = useSociety(societyId)
  const { id = '' } = society || {}
  const [expandedSections, setExpandedSections] = useState({})
  const prevSocietyRef = useRef(null)

  // Monitora i cambiamenti dei flag e aggiorna automaticamente i campi collegati
  useEffect(() => {
    if (!society) {
      return
    }

    // Inizializza il ref al primo caricamento
    if (!prevSocietyRef.current) {
      prevSocietyRef.current = society
      return
    }

    // Trova tutti i campi flag che sono cambiati (solo i flag, non i campi collegati)
    const changedFlags = new Set()
    Object.keys(fieldSections).forEach((sectionName) => {
      Object.entries(fieldSections[sectionName]).forEach(([key, fieldConfig]) => {
        const currentValue = society[key]
        const prevValue = prevSocietyRef.current?.[key]

        // Se il valore è cambiato e il campo non ha linkTo (è un flag)
        if (currentValue !== prevValue && !fieldConfig.linkTo) {
          changedFlags.add(key)
        }
      })
    })

    // Per ogni flag cambiato, aggiorna i campi collegati
    if (changedFlags.size > 0) {
      changedFlags.forEach((flagKey) => {
        Object.keys(fieldSections).forEach((sectionName) => {
          Object.entries(fieldSections[sectionName]).forEach(([fieldKey, fieldConfig]) => {
            if (fieldConfig.linkTo && fieldConfig.linkTo.flag === flagKey) {
              const flagValue = society[flagKey]
              const { offValue } = fieldConfig.linkTo

              // Se il flag è false (0), imposta il campo collegato a offValue
              if (flagValue === 0 || flagValue === false) {
                setSociety(fieldKey, offValue)
              }
            }
          })
        })
      })
    }

    // Aggiorna il ref dopo aver processato i cambiamenti
    prevSocietyRef.current = society
  }, [society, setSociety])

  const toggleSection = (sectionName) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionName]: !prev[sectionName]
    }))
  }

  const expandAllSections = () => {
    const allSections = Object.keys(fieldSections)
    const expandedState = {}
    allSections.forEach((section) => {
      expandedState[section] = true
    })
    setExpandedSections(expandedState)
  }

  const collapseAllSections = () => {
    const allSections = Object.keys(fieldSections)
    const collapsedState = {}
    allSections.forEach((section) => {
      collapsedState[section] = false
    })
    setExpandedSections(collapsedState)
  }

  /**
   * Sincronizza tutti i campi collegati in base ai valori dei loro flag
   * Questa funzione garantisce che prima del salvataggio tutti i campi collegati
   * abbiano il valore corretto in base ai loro flag
   * @param {Object} currentSociety - Oggetto società corrente
   * @returns {Object} Oggetto società con i campi collegati sincronizzati
   */
  const syncLinkedFields = (currentSociety) => {
    if (!currentSociety) return currentSociety

    const syncedSociety = { ...currentSociety }

    // Itera su tutte le sezioni e i campi
    Object.keys(fieldSections).forEach((sectionName) => {
      Object.entries(fieldSections[sectionName]).forEach(([fieldKey, fieldConfig]) => {
        // Se il campo ha un linkTo, controlla il flag
        if (fieldConfig.linkTo) {
          const { flag, offValue } = fieldConfig.linkTo
          const flagValue = syncedSociety[flag]

          // Se il flag è false (0), imposta il campo collegato a offValue
          if (flagValue === 0 || flagValue === false) {
            syncedSociety[fieldKey] = offValue
          }
        }
      })
    })

    return syncedSociety
  }

  const save = async () => {
    // Sincronizza i campi collegati prima del salvataggio
    const syncedSociety = syncLinkedFields(society)
    const res = await saveSociety(syncedSociety)
    res?.id && navigate('/admin/society/list')
    return res
  }

  if (!isSuccess || !society) return <Spinner />

  const isNew = id === 'new'

  const renderField = (key, fieldConfig) => {
    // Controlla se il campo deve essere visibile in base a linkTo
    if (!isFieldVisible(fieldConfig, society)) {
      return null
    }

    if (fieldConfig.type === 'array') {
      // Normalizza il valore per assicurarsi che sia sempre un array
      const arrayValue = Array.isArray(society[key])
        ? society[key]
        : society[key] != null
          ? typeof society[key] === 'string'
            ? society[key].split(',').map((v) => v.trim())
            : []
          : []

      return (
        <div key={key} className="nfield admin-field-container" style={{ gridColumn: 'span 6' }}>
          <label className="admin-field-label">{fieldConfig.label}</label>
          <div className="admin-field-description">{fieldConfig.description}</div>
          <Input
            value={arrayValue.join(',')}
            id={key}
            onChange={({ [key]: value }) =>
              setSociety(
                key,
                value.split(',').map((v) => v.trim())
              )
            }
            placeholder={fieldConfig.description}
          />
        </div>
      )
    }

    if (fieldConfig.type === 'boolean') {
      return (
        <div key={key} className="nfield admin-field-container" style={{ gridColumn: 'span 6' }}>
          <label className="admin-field-label">{fieldConfig.label}</label>
          <div className="admin-field-description">{fieldConfig.description}</div>

          <BooleanSelect
            value={society[key]}
            onChange={({ [key]: value }) => setSociety(key, value)}
            id={key}
          />
        </div>
      )
    }

    if (fieldConfig.type === 'json') {
      return (
        <div key={key} className="nfield admin-field-container" style={{ gridColumn: 'span 6' }}>
          <label className="admin-field-label">{fieldConfig.label}</label>
          <div className="admin-field-description">{fieldConfig.description}</div>
          <Input
            value={society[key] ? JSON.stringify(society[key], null, 2) : ''}
            id={key}
            onChange={({ [key]: value }) => {
              try {
                const parsedValue = value ? JSON.parse(value) : null
                setSociety(key, parsedValue)
              } catch (error) {
                // Se il JSON non è valido, mantieni il valore come stringa per permettere la correzione
                setSociety(key, value)
              }
            }}
            placeholder={fieldConfig.description}
            multiline
            rows={6}
          />
        </div>
      )
    }

    if (fieldConfig.type === 'enum') {
      return (
        <div key={key} className="nfield admin-field-container" style={{ gridColumn: 'span 6' }}>
          <label className="admin-field-label">{fieldConfig.label}</label>
          <div className="admin-field-description">{fieldConfig.description}</div>
          <Input
            dropdown
            value={society[key]}
            onChange={({ [key]: value }) => setSociety(key, value)}
            id={key}
            options={fieldConfig.options}
            optionLabel="label"
            optionValue="value"
            placeholder={t.seleziona}
          />
        </div>
      )
    }

    return (
      <div key={key} className="nfield admin-field-container" style={{ gridColumn: 'span 6' }}>
        <label className="admin-field-label">{fieldConfig.label}</label>
        <div className="admin-field-description">{fieldConfig.description}</div>
        <Input
          value={society[key]}
          onChange={({ [key]: value }) => setSociety(key, value)}
          id={key}
          placeholder={fieldConfig.description}
        />
      </div>
    )
  }

  return (
    <div className="admin-container admin-page--editor">
      <PageHeader
        className="admin-header"
        title={isNew ? t.admCreaSocieta : t.admModificaSocieta}
        subtitle={t.admSottotitoloSocieta}
        actions={
          <AdminEditorActions
            onBack={() => navigate('/admin/society/list')}
            onSave={save}
            backLabel={t.tornaIndietro}
            saveLabel={t.salva}
          />
        }
      />

      <div className="admin-section-controls">
        <button type="button" className="nbtn nbtn--sm nbtn--ghost" onClick={expandAllSections}>
          <i className="pi pi-angle-down" style={{ fontSize: 12, color: 'var(--primary)' }} />
          {t.admEspandiTutto}
        </button>
        <button type="button" className="nbtn nbtn--sm nbtn--ghost" onClick={collapseAllSections}>
          <i className="pi pi-angle-up" style={{ fontSize: 12, color: 'var(--primary)' }} />
          {t.admChiudiTutto}
        </button>
      </div>

      <div className="admin-sections-container">
        {Object.entries(fieldSections).map(([sectionName, fields]) => {
          const isExpanded = expandedSections[sectionName] !== false
          return (
            <div key={sectionName} className="nsec admin-section-card">
              <div
                className="nsec__head admin-section-header"
                onClick={() => toggleSection(sectionName)}
                style={{ cursor: 'pointer' }}
              >
                <div className="nsec__ico">
                  <i className="pi pi-cog" style={{ fontSize: 16 }} />
                </div>
                <h3 className="nsec__title">{sectionDisplayNames[sectionName] || sectionName}</h3>
                <span className="admin-section-toggle">{isExpanded ? '▼' : '▶'}</span>
              </div>
              {isExpanded && (
                <div className="nsec__body admin-section-content">
                  <div className="nrow admin-fields-container">
                    {Object.entries(fields).map(([key, fieldConfig]) =>
                      renderField(key, fieldConfig)
                    )}
                  </div>
                </div>
              )}
            </div>
          )
        })}
      </div>

      <div className="admin-actions">
        <button type="button" className="nbtn nbtn--primary" onClick={save}>
          <i className="pi pi-check" style={{ fontSize: 14 }} />
          {t.salva}
        </button>
        <button type="button" className="nbtn" onClick={() => navigate('/admin/society/list')}>
          <i className="pi pi-arrow-left" style={{ fontSize: 14 }} />
          {t.tornaIndietro}
        </button>
      </div>
    </div>
  )
}
